/* MENTALLIC™ — Smart Ring · 3D charging case with feature callouts.
   Sequence (plays when the section scrolls into view): turntable spin → lid opens →
   ring lifts → callout lines grow out of the case one by one. Then a gentle idle sway. */
// versioned URL from Liquid so a theme update is never served from a stale CDN copy
const core = await import(window.__mnrCore || './mnr-ring-core.js').catch((e) => { console.warn('[mnr] core', e); return null; });
const { hasWebGL, makeRenderer, studioEnv, buildRing } = core || {};

const stage = document.getElementById('mnrCaseStage');
const canvas = document.getElementById('mnrCase3d');

function roundedRect(THREE, w, d, r) {
  const s = new THREE.Shape(), x = -w / 2, y = -d / 2;
  s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + d - r); s.quadraticCurveTo(x + w, y + d, x + w - r, y + d);
  s.lineTo(x + r, y + d); s.quadraticCurveTo(x, y + d, x, y + d - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

function slab(THREE, w, d, r, height, bevel) {
  const g = new THREE.ExtrudeGeometry(roundedRect(THREE, w - bevel * 2, d - bevel * 2, r), {
    depth: height - bevel * 2, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 8, curveSegments: 32
  });
  g.rotateX(-Math.PI / 2);          // extrude axis → +Y
  g.translate(0, bevel, 0);         // bottom sits on y = 0
  g.computeVertexNormals();
  return g;
}

const ease = {
  out: (p) => 1 - Math.pow(1 - p, 3),
  inOut: (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2)
};
const clamp01 = (v) => Math.max(0, Math.min(1, v));

async function init() {
  if (!stage || !canvas || !core || !hasWebGL()) { stage && stage.classList.add('is-static'); return; }
  const THREE = await import('./mnr-three.min.js');
  const renderer = makeRenderer(THREE, canvas);
  const scene = new THREE.Scene();
  scene.environment = studioEnv(THREE, renderer);
  const key = new THREE.DirectionalLight(0xffffff, 1.2); key.position.set(-2, 4, 3); scene.add(key);

  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 50);
  camera.position.set(0, 2.3, 4.25);
  camera.lookAt(0, 0.47, 0);

  /* ── Case ── */
  const W = 1.16, BASE_H = 0.42, LID_H = 0.2, RAD = 0.36, BEV = 0.055;
  // glossy ABS without a clearcoat layer (a coat on top read as plastic film)
  const plastic = new THREE.MeshPhysicalMaterial({ color: 0x111113, roughness: 0.2, metalness: 0, specularIntensity: 0.7 });
  const glossDark = new THREE.MeshPhysicalMaterial({ color: 0x070708, roughness: 0.12, metalness: 0, specularIntensity: 0.8 });
  const magnet = new THREE.MeshPhysicalMaterial({ color: 0xb9bcc0, roughness: 0.25, metalness: 1 });

  const caseGroup = new THREE.Group(); scene.add(caseGroup);
  const base = new THREE.Mesh(slab(THREE, W, W, RAD, BASE_H, BEV), plastic); caseGroup.add(base);

  // well + charging post
  const well = new THREE.Mesh(new THREE.CircleGeometry(0.43, 96), glossDark);
  well.rotation.x = -Math.PI / 2; well.position.y = BASE_H + 0.002; caseGroup.add(well);
  const wellRim = new THREE.Mesh(new THREE.TorusGeometry(0.43, 0.012, 16, 96), plastic);
  wellRim.rotation.x = Math.PI / 2; wellRim.position.y = BASE_H + 0.004; caseGroup.add(wellRim);
  // small magnetic charging post, set toward the hinge: the ring slips over it and rests against it
  const POST_R = 0.1, POST_Z = -0.2;
  const post = new THREE.Mesh(new THREE.CylinderGeometry(POST_R, POST_R * 1.08, 0.2, 64), plastic);
  post.position.set(0, BASE_H + 0.1, POST_Z); caseGroup.add(post);
  const postTop = new THREE.Mesh(new THREE.CircleGeometry(POST_R, 64), glossDark);
  postTop.rotation.x = -Math.PI / 2; postTop.position.set(0, BASE_H + 0.201, POST_Z); caseGroup.add(postTop);
  const pin = new THREE.Mesh(new THREE.CircleGeometry(POST_R * 0.42, 48), magnet);
  pin.rotation.x = -Math.PI / 2; pin.position.set(0, BASE_H + 0.202, POST_Z); caseGroup.add(pin);

  // status LED on the front face
  const ledMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0.25, 2.2, 0.8) });
  const led = new THREE.Mesh(new THREE.CircleGeometry(0.016, 24), ledMat);
  led.position.set(0, BASE_H * 0.62, W / 2 + 0.001); caseGroup.add(led);
  const ledGlow = new THREE.Mesh(new THREE.CircleGeometry(0.05, 24), new THREE.MeshBasicMaterial({ color: 0x2ee57a, transparent: true, opacity: 0.25, depthWrite: false }));
  ledGlow.position.copy(led.position).add(new THREE.Vector3(0, 0, 0.001)); caseGroup.add(ledGlow);

  // lid hinged on the back edge
  const lidPivot = new THREE.Group(); lidPivot.position.set(0, BASE_H + 0.004, -W / 2 + BEV); caseGroup.add(lidPivot);
  const lid = new THREE.Mesh(slab(THREE, W, W, RAD, LID_H, BEV), plastic);
  lid.position.set(0, 0, W / 2 - BEV); lidPivot.add(lid);
  const mirror = new THREE.Mesh(new THREE.CircleGeometry(0.44, 96), glossDark);
  mirror.rotation.x = Math.PI / 2; mirror.position.set(0, -0.001, W / 2 - BEV); lidPivot.add(mirror);

  // ring resting on the post (follows the selected finish)
  const ring = buildRing(THREE);
  const RS = 0.355, RING_Y = BASE_H + 0.21 * RS + 0.004;
  ring.group.scale.setScalar(RS); ring.group.position.y = RING_Y; caseGroup.add(ring.group);

  /* ── Callouts (HTML labels + SVG leader lines, anchored to projected 3D points) ── */
  const svg = stage.querySelector('.mnr-case__lines');
  const labels = Array.prototype.slice.call(stage.querySelectorAll('.mnr-case__label'));
  const items = Array.prototype.slice.call(document.querySelectorAll('#mnrCaseList li'));
  const anchors = [
    () => new THREE.Vector3(-0.3, ring.group.position.y + 0.07, 0.14),   // ring
    () => new THREE.Vector3(W / 2 - 0.02, BASE_H * 0.45, -0.05),          // battery body
    () => led.position.clone()                                           // status light
  ];
  const lines = labels.map(() => {
    const pl = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('r', '3.5'); svg.appendChild(pl); svg.appendChild(dot);
    return { pl, dot };
  });
  const v = new THREE.Vector3();
  function placeCallouts(progress) {
    const box = stage.getBoundingClientRect();
    svg.setAttribute('viewBox', '0 0 ' + box.width + ' ' + box.height);
    labels.forEach((lab, i) => {
      const p = clamp01(progress[i]);
      v.copy(anchors[i]()); caseGroup.localToWorld(v); v.project(camera);
      const ax = (v.x * 0.5 + 0.5) * box.width, ay = (-v.y * 0.5 + 0.5) * box.height;
      const lb = lab.getBoundingClientRect();
      const right = lab.classList.contains('is-right');
      const lx = (right ? lb.left : lb.right) - box.left, ly = lb.top - box.top + lb.height / 2;
      const ex = lx + (right ? -22 : 22);
      const pts = [[ax, ay], [ex, ly], [lx, ly]];
      const len = Math.hypot(ex - ax, ly - ay) + Math.abs(lx - ex);
      lines[i].pl.setAttribute('points', pts.map((q) => q.join(',')).join(' '));
      lines[i].pl.style.strokeDasharray = len;
      lines[i].pl.style.strokeDashoffset = len * (1 - ease.out(clamp01(p * 1.6)));
      lines[i].dot.setAttribute('cx', ax); lines[i].dot.setAttribute('cy', ay);
      lines[i].dot.style.opacity = p > 0 ? 1 : 0;
      const shown = p > 0.55;
      lab.classList.toggle('is-on', shown);
      if (items[i]) items[i].classList.toggle('is-on', shown);
    });
  }

  function resize() {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(canvas); resize();

  /* ── Timeline ── */
  const SETTLE = -0.55, LID_OPEN = -1.95;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let t0 = null, visible = false, last = performance.now();
  const replay = stage.querySelector('.mnr-case__replay');

  function poseAt(t) {
    const spin = ease.out(clamp01(t / 1.7));
    const lidP = ease.inOut(clamp01((t - 1.5) / 1.0));
    const lift = ease.inOut(clamp01((t - 2.3) / 0.8));
    return {
      rotY: SETTLE - Math.PI * 1.6 * (1 - spin),
      lid: LID_OPEN * lidP,
      ringY: RING_Y + 0.025 * lift,             // seated on the magnetic post — only a soft settle, no floating
      ringSpin: lift * 0.35,
      callouts: [ (t - 3.2) / 0.7, (t - 3.9) / 0.7, (t - 4.6) / 0.7 ]
    };
  }

  function frame(now) {
    requestAnimationFrame(frame);
    if (!visible || document.hidden) { last = now; return; }
    const dt = Math.min((now - last) / 1000, 0.05); last = now;
    let pose;
    if (t0 === null) pose = poseAt(0);
    else {
      const t = reduce ? 99 : (now - t0) / 1000;
      pose = poseAt(t);
      if (t > 5.4) { // idle sway after the reveal
        pose.rotY = SETTLE + Math.sin((t - 5.4) * 0.45) * 0.14;
        replay && replay.classList.add('is-on');
      } else ring.group.rotation.y = pose.ringSpin;
    }
    caseGroup.rotation.y = pose.rotY;
    lidPivot.rotation.x = pose.lid;
    ring.group.position.y = pose.ringY;
    ring.liner.emissiveIntensity = 1.3 + Math.sin(now / 380) * 0.35;
    ledGlow.material.opacity = 0.18 + (Math.sin(now / 600) * 0.5 + 0.5) * 0.25;
    renderer.render(scene, camera);
    placeCallouts(pose.callouts);
  }

  new IntersectionObserver((es) => {
    visible = es[0].isIntersecting;
    if (visible && t0 === null && es[0].intersectionRatio > 0.35) t0 = performance.now();
  }, { threshold: [0, 0.35] }).observe(stage);
  replay && replay.addEventListener('click', () => { t0 = performance.now(); replay.classList.remove('is-on'); });

  stage.classList.add('is-3d');
  requestAnimationFrame(frame);
}

function boot() {
  if (!stage) return;
  // start loading only when the section gets close to the viewport
  const io = new IntersectionObserver((es) => {
    if (!es[0].isIntersecting) return;
    io.disconnect();
    init().catch((e) => { console.warn('[mnr-case-3d] fallback', e); stage.classList.add('is-static'); });
  }, { rootMargin: '400px 0px' });
  io.observe(stage);
}
if (document.readyState === 'complete') boot(); else window.addEventListener('load', boot, { once: true });
